import React, { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useReducedMotion } from 'framer-motion';
import type { LibraryCard } from '@/lib/library/libraryCard';
import { computeLayout, rectAt, rectsInRange, springEasing, type GridLayout, type TileRect } from '@/lib/library/layout';
import { LibraryTile } from './LibraryTile';
import { LibraryDetail, useLibraryDetailPresence } from './LibraryDetail';
import { useLibrary } from './LibraryContext';
import { LibraryTileMenu, type TileMenuAction, type TileMenuTarget } from './LibraryTileMenu';
import { LibraryCommentsSheet, LibrarySpacePicker } from './LibrarySheets';
import { shareCard } from '@/lib/library/shareCard';
import { readStoredColumns, storeColumns, supportsLinearEasing, usePinchColumns, type PinchCommit } from './usePinchColumns';

export interface LibraryGridProps {
  cards: LibraryCard[];
  /** Rendered above the first row, inside the scroll area (e.g. a collection title). */
  header?: React.ReactNode;
  /** Shown instead of the grid when `cards` is empty. */
  emptyState?: React.ReactNode;
  /**
   * 'document': the page itself scrolls (the Everything feed), so Safari can
   * tuck its address bar away. The grid then pads its top by
   * --library-top-inset for the fixed header. 'element' (default): the grid
   * fills its parent and scrolls inside it.
   */
  scrollMode?: 'element' | 'document';
}

/**
 * One way to read and move the scroll, whether the grid scrolls inside itself
 * or the page scrolls it: where it is, how tall the view is, where the tile
 * layer starts and the box on screen everything is measured against.
 */
interface ScrollHost {
  top: () => number;
  setTop: (value: number) => void;
  height: () => number;
  layerTop: () => number;
  box: () => DOMRect;
  target: () => HTMLElement | Window | null;
}

/** Hold this long without moving to open a tile's actions. */
const LONG_PRESS_MS = 420;
/** Moving further than this (px) before then is a scroll, not a press. */
const LONG_PRESS_SLOP = 10;

/** Re-pick the mounted tiles once the scroll has moved this share of a screen. */
const RANGE_STEP = 0.25;
/** Tiles stay mounted this many screens above and below the viewport. */
const OVERSCAN = 1;

interface PendingReflow {
  old: GridLayout;
  commit: PinchCommit;
  oldScroll: number;
  anchor: TileRect | null;
  /** Where the fingers were inside the anchor tile, as fractions of its size. */
  fx: number;
  fy: number;
  to: number;
  scrolled: boolean;
  newScroll: number;
}

interface DetailState {
  id: string;
  originRect: DOMRect | null;
}

const GridCell = memo(function GridCell({
  card,
  rect,
  columns,
  compact,
  hidden,
  commentCount,
  onOpen,
}: {
  card: LibraryCard;
  rect: TileRect;
  columns: number;
  compact: boolean;
  hidden: boolean;
  commentCount: number;
  onOpen: (card: LibraryCard, element: HTMLElement) => void;
}) {
  return (
    <div
      data-tile-id={card.id}
      className="absolute"
      style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height, transformOrigin: '0 0', visibility: hidden ? 'hidden' : undefined }}
    >
      <LibraryTile card={card} compact={compact} columns={columns} commentCount={commentCount} onOpen={onOpen} />
    </div>
  );
});

/**
 * The Library's scrolling grid. Fills its parent and owns the scroll.
 *
 * Layout is computed (src/lib/library/layout.ts), so only tiles near the
 * viewport mount, and a pinch can reflow every tile from its old rect to
 * its new one. Two fingers step between 2 and 5 columns; a tap opens the
 * detail view, which expands from the tile and flies back into it.
 */
export function LibraryGrid({ cards, header, emptyState, scrollMode = 'element' }: LibraryGridProps) {
  const reduce = useReducedMotion() ?? false;
  const scrollRef = useRef<HTMLDivElement>(null);
  const layerRef = useRef<HTMLDivElement>(null);
  const pageScroll = scrollMode === 'document';
  const host = useMemo<ScrollHost>(
    () =>
      pageScroll
        ? {
            top: () => window.scrollY,
            setTop: (value) => window.scrollTo(0, value),
            height: () => window.innerHeight,
            layerTop: () => (layerRef.current ? layerRef.current.getBoundingClientRect().top + window.scrollY : 0),
            box: () => new DOMRect(0, 0, window.innerWidth, window.innerHeight),
            target: () => window,
          }
        : {
            top: () => scrollRef.current?.scrollTop ?? 0,
            setTop: (value) => {
              if (scrollRef.current) scrollRef.current.scrollTop = value;
            },
            height: () => scrollRef.current?.clientHeight ?? 0,
            layerTop: () => layerRef.current?.offsetTop ?? 0,
            box: () => scrollRef.current?.getBoundingClientRect() ?? new DOMRect(),
            target: () => scrollRef.current,
          },
    [pageScroll],
  );
  const [columns, setColumns] = useState(readStoredColumns);
  const [width, setWidth] = useState(0);
  const [range, setRange] = useState({ top: 0, height: 844 });
  const [detail, setDetail] = useState<DetailState | null>(null);
  const [detailCardId, setDetailCardId] = useState<string | null>(null);
  const [detailClosing, setDetailClosing] = useState(false);
  const setChromeHidden = useLibraryDetailPresence();
  const { actions, comments } = useLibrary();
  const commentCounts = comments.counts;

  const layout = useMemo(() => computeLayout(cards, { width, columns, top: 4 }), [cards, width, columns]);
  const layoutRef = useRef(layout);
  const rangeRef = useRef(range);
  const pendingRef = useRef<PendingReflow | null>(null);

  useLayoutEffect(() => {
    layoutRef.current = layout;
    rangeRef.current = range;
  }, [layout, range]);

  // Width: measure once before first paint, then follow width changes only
  // (CLAUDE.md rule 1: never react to our own height changes).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let lastWidth = el.clientWidth;
    setWidth(lastWidth);
    setRange({ top: host.top() - host.layerTop(), height: host.height() || 844 });
    let timer = 0;
    const observer = new ResizeObserver((entries) => {
      const next = entries[0].contentRect.width;
      if (Math.abs(next - lastWidth) <= 1) return;
      lastWidth = next;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setWidth(el.clientWidth), 50);
    });
    observer.observe(el);
    return () => {
      window.clearTimeout(timer);
      observer.disconnect();
    };
  }, [host]);

  // Which tiles are mounted: re-picked when the scroll drifts a quarter screen.
  const updateRange = useCallback((force = false) => {
    if (!scrollRef.current) return;
    const top = host.top() - host.layerTop();
    const height = host.height() || rangeRef.current.height;
    const prev = rangeRef.current;
    if (force || Math.abs(top - prev.top) > height * RANGE_STEP || Math.abs(height - prev.height) > 1) {
      const next = { top, height };
      rangeRef.current = next;
      setRange(next);
    }
  }, [host]);

  useEffect(() => {
    const el = host.target();
    if (!el) return;
    let frame = 0;
    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        updateRange();
      });
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    // The page's height changes with the address bar: re-pick on resize too
    if (pageScroll) window.addEventListener('resize', onScroll);
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (pageScroll) window.removeEventListener('resize', onScroll);
      cancelAnimationFrame(frame);
    };
  }, [host, pageScroll, updateRange]);

  // Long press: hold a tile still to open its actions (share, save to a space,
  // comment, delete). Moving first is a scroll and a second finger is a pinch; once it
  // has opened, the page stops scrolling and the lift that ends it is not a tap.
  const [menu, setMenu] = useState<TileMenuTarget | null>(null);
  const [sheet, setSheet] = useState<{ kind: 'save' | 'comment'; card: LibraryCard } | null>(null);
  const suppressOpenUntil = useRef(0);
  const cardByIdRef = useRef<Map<string, LibraryCard>>(new Map());
  const radiusRef = useRef(14);
  const menuOpenRef = useRef(false);
  useEffect(() => {
    menuOpenRef.current = !!menu;
  }, [menu]);

  const openMenu = useCallback((id: string, byTouch: boolean) => {
    const card = cardByIdRef.current.get(id);
    const media = scrollRef.current?.querySelector<HTMLElement>(`[data-tile-id="${CSS.escape(id)}"] [data-library-media]`);
    if (!card || !media || menuOpenRef.current) return;
    setMenu({ card, rect: media.getBoundingClientRect(), radius: radiusRef.current, byTouch });
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let timer = 0;
    let start: { x: number; y: number } | null = null;
    let fired = false;
    const cancel = () => {
      window.clearTimeout(timer);
      timer = 0;
      start = null;
    };
    const onStart = (e: TouchEvent) => {
      cancel();
      fired = false;
      if (e.touches.length !== 1 || menuOpenRef.current) return;
      const t = e.touches[0];
      const id = (t.target as Element | null)?.closest?.<HTMLElement>('[data-tile-id]')?.dataset.tileId;
      if (!id) return;
      start = { x: t.clientX, y: t.clientY };
      timer = window.setTimeout(() => {
        timer = 0;
        fired = true;
        suppressOpenUntil.current = performance.now() + 800;
        // iOS may already have started a text selection under the finger
        window.getSelection()?.removeAllRanges();
        navigator.vibrate?.(8);
        openMenu(id, true);
      }, LONG_PRESS_MS);
    };
    const onMove = (e: TouchEvent) => {
      if (fired) {
        // The finger is choosing an action now: the page must not scroll under it
        if (e.cancelable) e.preventDefault();
        return;
      }
      if (!start) return;
      const t = e.touches[0];
      if (e.touches.length > 1 || !t || Math.hypot(t.clientX - start.x, t.clientY - start.y) > LONG_PRESS_SLOP) cancel();
    };
    const onEnd = (e: TouchEvent) => {
      cancel();
      if (fired) {
        // No click (and no detail view) from the lift that ends a long press
        if (e.cancelable) e.preventDefault();
        fired = false;
      }
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: false });
    el.addEventListener('touchcancel', onEnd, { passive: false });
    // Mouse and trackpad: the context menu opens the same actions
    const onContext = (e: MouseEvent) => {
      const id = (e.target as Element | null)?.closest?.<HTMLElement>('[data-tile-id]')?.dataset.tileId;
      if (!id) return;
      e.preventDefault();
      if (!fired) openMenu(id, false);
    };
    el.addEventListener('contextmenu', onContext);
    return () => {
      cancel();
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
      el.removeEventListener('contextmenu', onContext);
    };
  }, [openMenu]);

  const handleMenuAction = useCallback(
    (action: TileMenuAction, card: LibraryCard) => {
      if (action === 'share') void shareCard(card);
      // Asks first (deleteDiscoverItem's confirm), then refreshes the grid
      else if (action === 'delete') void actions.deleteItem(card.item);
      else setSheet({ kind: action, card });
    },
    [actions],
  );

  // Tiles under a finger stay mounted until the finger lifts: touch events
  // keep targeting the touchstart node, and a detached node no longer
  // bubbles them to the grid, which would strand a pinch mid-gesture.
  const touchedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onStart = (e: TouchEvent) => {
      for (const t of Array.from(e.changedTouches)) {
        const id = (t.target as Element | null)?.closest?.<HTMLElement>('[data-tile-id]')?.dataset.tileId;
        if (id) touchedRef.current.add(id);
      }
    };
    const onEnd = (e: TouchEvent) => {
      if (e.touches.length === 0) touchedRef.current.clear();
    };
    el.addEventListener('touchstart', onStart, { passive: true, capture: true });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart, { capture: true });
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, []);

  const visible = useMemo(() => {
    const rects = rectsInRange(layout, range.top - range.height * OVERSCAN, range.top + range.height * (1 + OVERSCAN));
    const touched = touchedRef.current;
    if (touched.size === 0) return rects;
    const extra: TileRect[] = [];
    touched.forEach((id) => {
      const r = layout.byId.get(id);
      if (r && !rects.includes(r)) extra.push(r);
    });
    return extra.length ? [...rects, ...extra] : rects;
  }, [layout, range]);
  const cardById = useMemo(() => new Map(cards.map((c) => [c.id, c])), [cards]);
  useEffect(() => {
    cardByIdRef.current = cardById;
    radiusRef.current = layout.density.radius;
  }, [cardById, layout.density.radius]);

  // Pinch: take a step. Record where everything was, then render the new
  // column count synchronously so the layout effect below can FLIP.
  const handleCommit = useCallback((commit: PinchCommit) => {
    const el = scrollRef.current;
    const old = layoutRef.current;
    if (!el) return;
    const anchor = rectAt(old, commit.origin.x, commit.origin.y);
    pendingRef.current = {
      old,
      commit,
      oldScroll: host.top(),
      anchor,
      fx: anchor ? Math.min(1, Math.max(0, (commit.origin.x - anchor.x) / (anchor.width || 1))) : 0.5,
      fy: anchor ? Math.min(1, Math.max(0, (commit.origin.y - anchor.y) / (anchor.height || 1))) : 0.5,
      to: commit.to,
      scrolled: false,
      newScroll: host.top(),
    };
    flushSync(() => setColumns(commit.to));
    storeColumns(commit.to);
  }, [host]);

  usePinchColumns({ scrollRef, layerRef, columns, onCommit: handleCommit, reducedMotion: reduce, enabled: cards.length > 0, getBox: host.box });

  // After a step: keep the tile under the fingers anchored (scrollTop), then
  // animate every mounted tile from its old rect to its new one.
  useLayoutEffect(() => {
    const p = pendingRef.current;
    const el = scrollRef.current;
    const layer = layerRef.current;
    if (!p || !el || !layer || layout.density.columns !== p.to) return;

    if (!p.scrolled) {
      p.scrolled = true;
      const layerTop = host.layerTop();
      const anchorRect = p.anchor ? layout.byId.get(p.anchor.id) : null;
      if (anchorRect) {
        const anchorY = anchorRect.y + p.fy * anchorRect.height;
        host.setTop(Math.max(0, layerTop + anchorY - p.commit.focus.y));
      }
      p.newScroll = host.top();
      const top = p.newScroll - layerTop;
      if (Math.abs(top - rangeRef.current.top) > 1) {
        const next = { top, height: rangeRef.current.height };
        rangeRef.current = next;
        setRange(next); // re-renders before paint; the FLIP runs on that pass
        return;
      }
    }

    pendingRef.current = null;
    if (typeof layer.animate !== 'function') return;

    if (reduce) {
      layer.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 180, easing: 'ease-out' });
      return;
    }

    const { commit, old, oldScroll, newScroll } = p;
    const { scale: s, origin: o, translate: t } = commit;
    const spring = springEasing(500, 38);
    const easing = supportsLinearEasing() ? spring.easing : 'cubic-bezier(.22, 1, .36, 1)';
    const cells = layer.querySelectorAll<HTMLElement>('[data-tile-id]');
    cells.forEach((cell) => {
      const id = cell.dataset.tileId!;
      const next = layout.byId.get(id);
      const prev = old.byId.get(id);
      if (!next) return;
      if (!prev) {
        cell.animate([{ opacity: 0 }, { opacity: 1 }], { duration: 160, easing: 'ease-out' });
        return;
      }
      // Where the tile was on screen: old rect under the live pinch transform
      const visX = o.x + t.x + s * (prev.x - o.x);
      const visY = o.y + t.y + s * (prev.y - o.y) - oldScroll;
      const dx = visX - next.x;
      const dy = visY - (next.y - newScroll);
      const k = (s * prev.width) / (next.width || 1);
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(k - 1) < 0.002) return;
      cell.animate([{ transform: `translate(${dx}px, ${dy}px) scale(${k})` }, { transform: 'none' }], { duration: spring.duration, easing });
    });
    // Runs on the column change, and again on the range change it may cause
  }, [host, layout, range, reduce]);

  // A few cards changed (something saved, deleted, or a preview arriving and
  // resizing its tile): the others glide from where they were to where they
  // now sit, and new ones settle in, instead of everything jumping. The first
  // load reveals the mounted tiles top to bottom. Bigger changes (a search, a
  // filter, a pinch step, a rotation) just appear: the pinch has its own FLIP.
  const prevLayoutRef = useRef<GridLayout | null>(null);
  useLayoutEffect(() => {
    const prev = prevLayoutRef.current;
    prevLayoutRef.current = layout;
    const layer = layerRef.current;
    if (!prev || reduce || !layer || typeof layer.animate !== 'function') return;
    if (prev.density.columns !== layout.density.columns || Math.abs(prev.width - layout.width) > 1) return;
    const spring = springEasing(500, 38);
    const easing = supportsLinearEasing() ? spring.easing : 'cubic-bezier(.22, 1, .36, 1)';
    const cells = Array.from(layer.querySelectorAll<HTMLElement>('[data-tile-id]'));

    if (prev.rects.length === 0 && layout.rects.length > 0) {
      // Your mind arrives: tiles rise in, row by row
      cells.forEach((cell) => {
        const r = layout.byId.get(cell.dataset.tileId ?? '');
        if (!r) return;
        cell.animate([{ opacity: 0, transform: 'translateY(16px) scale(0.98)' }, { opacity: 1, transform: 'none' }], {
          duration: spring.duration,
          easing,
          delay: Math.min(420, (r.y / Math.max(1, layout.height)) * 900 + r.x * 0.15),
          fill: 'backwards',
        });
      });
      return;
    }

    let added = 0;
    let removed = 0;
    for (const id of layout.byId.keys()) if (!prev.byId.has(id)) added++;
    for (const id of prev.byId.keys()) if (!layout.byId.has(id)) removed++;
    if (added + removed > 3) return;

    cells.forEach((cell) => {
      const id = cell.dataset.tileId ?? '';
      const next = layout.byId.get(id);
      if (!next) return;
      const was = prev.byId.get(id);
      if (!was) {
        // Just saved: it settles into its place
        cell.animate([{ opacity: 0, transform: 'translateY(-10px) scale(0.92)' }, { opacity: 1, transform: 'none' }], { duration: spring.duration, easing });
        return;
      }
      const dx = was.x - next.x;
      const dy = was.y - next.y;
      if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5) return;
      cell.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'none' }], { duration: spring.duration, easing });
    });
  }, [layout, reduce]);

  // Detail view
  const openDetail = useCallback((card: LibraryCard, element: HTMLElement) => {
    // The tap that ends a long press is not a tap
    if (performance.now() < suppressOpenUntil.current) return;
    setDetail({ id: card.id, originRect: element.getBoundingClientRect() });
    setDetailCardId(card.id);
    setDetailClosing(false);
  }, []);

  useEffect(() => {
    setChromeHidden(!!detail && !detailClosing);
  }, [detail, detailClosing, setChromeHidden]);
  useEffect(() => () => setChromeHidden(false), [setChromeHidden]);

  /** The tile's picture rect on screen, scrolling the grid first if the tile is out of view. */
  const getTileRect = useCallback((id: string): DOMRect | null => {
    const el = scrollRef.current;
    const layer = layerRef.current;
    const r = layoutRef.current.byId.get(id);
    if (!el || !layer || !r) return null;
    const box = host.box();
    const style = getComputedStyle(el);
    // The view the tile must sit in: under a fixed header (page scroll) and above the bottom bars
    const topInset = pageScroll ? parseFloat(style.paddingTop) || 0 : 0;
    const bottomInset = parseFloat(style.paddingBottom) || 0;
    const viewHeight = host.height() - bottomInset - topInset;
    let top = host.layerTop() + r.y - host.top() - topInset;
    if (top < 0 || top + r.mediaHeight > viewHeight) {
      host.setTop(Math.max(0, host.layerTop() + r.y - topInset - Math.max(0, (viewHeight - r.mediaHeight) / 2)));
      top = host.layerTop() + r.y - host.top() - topInset;
      flushSync(() => updateRange(true));
    }
    const layerBox = layer.getBoundingClientRect();
    return new DOMRect(layerBox.left + r.x, box.top + topInset + top, r.width, r.mediaHeight);
  }, [host, pageScroll, updateRange]);

  const handleDetailClose = useCallback(() => setDetailClosing(true), []);
  // Focus returns to the tile the view closed on, once it is visible again
  const refocusRef = useRef<string | null>(null);
  // The flight has landed on the tile: show the tile under the card, which then fades out over it
  const handleDetailLand = useCallback(() => {
    refocusRef.current = detailCardId;
    setDetailCardId(null);
  }, [detailCardId]);
  const handleDetailClosed = useCallback(() => {
    setDetail(null);
    setDetailCardId(null);
    setDetailClosing(false);
  }, []);
  useEffect(() => {
    const id = refocusRef.current;
    if (detail || !id) return;
    refocusRef.current = null;
    scrollRef.current?.querySelector<HTMLElement>(`[data-card-id="${CSS.escape(id)}"]`)?.focus({ preventScroll: true });
  }, [detail]);

  const compact = !layout.density.showTitles;

  return (
    <div
      ref={scrollRef}
      className={pageScroll ? 'relative' : 'relative h-full overflow-y-auto overflow-x-hidden overscroll-contain'}
      style={
        pageScroll
          ? { paddingTop: 'var(--library-top-inset, 0px)', paddingBottom: 'var(--library-bottom-inset, 24px)', touchAction: 'pan-y' }
          : { paddingBottom: 'var(--library-bottom-inset, 24px)', touchAction: 'pan-y', WebkitOverflowScrolling: 'touch' }
      }
    >
      {header}
      {cards.length === 0 ? (
        emptyState ?? null
      ) : (
        <div ref={layerRef} className="relative" style={{ height: layout.height, ['--library-tile-radius' as string]: `${layout.density.radius}px` }}>
          {width > 0 &&
              visible.map((rect) => {
                const card = cardById.get(rect.id);
                if (!card) return null;
                return (
                  <GridCell
                    key={rect.id}
                    card={card}
                    rect={rect}
                    columns={layout.density.columns}
                    compact={compact}
                    hidden={rect.id === detailCardId}
                    commentCount={commentCounts[rect.id] ?? 0}
                    onOpen={openDetail}
                  />
                );
              })}
        </div>
      )}

      {menu && <LibraryTileMenu target={menu} onAction={handleMenuAction} onClose={() => setMenu(null)} />}
      <LibrarySpacePicker card={sheet?.kind === 'save' ? sheet.card : null} open={sheet?.kind === 'save'} onOpenChange={(open) => !open && setSheet(null)} />
      <LibraryCommentsSheet card={sheet?.kind === 'comment' ? sheet.card : null} open={sheet?.kind === 'comment'} onOpenChange={(open) => !open && setSheet(null)} />

      {detail && (
        <LibraryDetail
          cards={cards}
          initialId={detail.id}
          originRect={detail.originRect}
          tileRadius={layout.density.radius}
          getTileRect={getTileRect}
          onCurrentChange={setDetailCardId}
          onClose={handleDetailClose}
          onLand={handleDetailLand}
          onClosed={handleDetailClosed}
        />
      )}
    </div>
  );
}
